"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import {
	type ReactNode,
	createRef,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { CheckIcon } from "../icon";
import { cn, isUndefined } from "../lib/utils";
import {
	type ProgressIndicatorOverrides,
	type ProgressIndicatorProps,
	type Status,
	computeInitialStatus,
	stati,
} from "./progress-indicator";
import { Spinner } from "./spinner";

const SIZE = 32;
const PADDING = 3;
const HALF_SIZE = (SIZE - PADDING * 2) / 2;
const STROKE_WIDTH = 3;
export const PROGRESS_THRESHOLD = 0.9;

const color = {
	surface: "#3D4662",
	surfaceSecondary: "#D8D3D2",
	success: "#5BB98B",
};

function BulletIndicator({
	isActive = false,
}: {
	isActive?: boolean;
}): React.ReactElement {
	return (
		<div
			className={cn(
				"w-[8px] h-[8px] rounded-full",
				isActive ? "bg-background" : "bg-message-surface-agent-disabled",
			)}
		/>
	);
}

function CircularIndicator({
	progress,
	duration,
}: {
	progress: number;
	duration: number;
}): React.ReactElement {
	const inner = createRef<SVGCircleElement>();
	const outer = createRef<SVGCircleElement>();

	const radius = HALF_SIZE - STROKE_WIDTH / 2;
	const circumference = 2 * Math.PI * radius;

	useGSAP(() => {
		const remainingDuration = duration * (1 - progress);

		gsap.set(outer.current, {
			r: radius,
			duration: 0.12,
			scale: 1,
			opacity: 1,
			y: 0,
			fill: "none",
			stroke: color.surfaceSecondary,
			strokeWidth: STROKE_WIDTH,
		});
		gsap.fromTo(
			"#inner",
			{
				strokeDasharray: `${circumference * progress} ${circumference}`,
			},
			{
				strokeDasharray: `${circumference * 0.9} ${circumference}`,
				duration: remainingDuration,
			},
		);
	}, [progress]);

	return (
		<div
			className="bg-background"
			style={{ padding: PADDING, width: SIZE, height: SIZE }}
		>
			<svg height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE}>
				<circle cx={HALF_SIZE} cy={HALF_SIZE} ref={outer} />
				<g
					style={{
						transform: "rotate(-90deg)",
						transformOrigin: `${HALF_SIZE}px ${HALF_SIZE}px`,
					}}
				>
					<circle
						className="stroke-foreground"
						cx={HALF_SIZE}
						cy={HALF_SIZE}
						fill="none"
						id="inner"
						r={radius}
						ref={inner}
						strokeDasharray={`${circumference * progress} ${circumference}`}
						strokeWidth={STROKE_WIDTH}
						style={{ transformOrigin: `${HALF_SIZE}px ${HALF_SIZE}px` }}
					/>
				</g>
			</svg>
		</div>
	);
}

function DoneIndicator(): React.ReactElement {
	return (
		<div className="flex justify-center items-center w-full h-full bg-background">
			<CheckIcon className="w-5 h-5 text-foreground" strokeWidth={2} />
		</div>
	);
}

function SpinnerIndicator(): React.ReactElement {
	return (
		<div
			className="bg-background"
			style={{
				padding: PADDING,
				width: SIZE,
				height: SIZE,
			}}
		>
			<Spinner color={color.surface} />
		</div>
	);
}

export function ProgressIndicatorBullet({
	progress,
	done,
	duration = 90,
	isActive,
	isCanceled,
	overrides = {},
}: ProgressIndicatorProps & {
	isActive?: boolean;
	overrides?: Partial<ProgressIndicatorOverrides>;
}): React.ReactElement {
	const p = progress ?? -1;
	const [status, setStatus] = useState(
		overrides.status ?? computeInitialStatus(done ? 1 : p, duration),
	);

	const hasMounted = useRef<boolean>(false);

	const transition = useCallback((newState: Status): void => {
		setStatus((state) => {
			const currentIndex = stati.indexOf(status);
			const nextIndex = stati.indexOf(newState);
			if (state === "success" || currentIndex >= nextIndex) return state;
			return newState;
		});
	}, []);

	// This checks the initial render
	useEffect(() => {
		if (status === "done") {
			transition("success");
		}

		hasMounted.current = true;
	}, []);

	useEffect(() => {
		let t: ReturnType<typeof setTimeout> | null = null;

		if (isUndefined(progress)) {
			return;
		}

		if (done) {
			transition("done");
			t = setTimeout(() => {
				transition("success");
			}, 1000);

			return () => {
				t && clearTimeout(t);
			};
		}

		if (progress === -1) {
			transition("pending");
			return;
		}

		if (p < 1 && duration !== null) {
			transition("in-progress");
		}
	}, [p, progress, transition, done, duration]);

	if (status === "success" || status === "done") {
		return (
			<div className="flex justify-center items-center w-8 h-8 rounded-full overflow-hidden">
				<DoneIndicator />
			</div>
		);
	}

	let body: ReactNode | null = <BulletIndicator isActive={done ?? isActive} />;

	if (
		status === "in-progress" &&
		isActive &&
		!isUndefined(duration) &&
		!isCanceled
	) {
		body = <CircularIndicator duration={duration} progress={p} />;
	}

	// this is for the subprocess that exceeded estimated completion time
	if (
		p === PROGRESS_THRESHOLD &&
		isActive &&
		!isUndefined(duration) &&
		!isCanceled
	) {
		body = <SpinnerIndicator />;
	}

	return (
		<div className="flex justify-center items-center w-8 h-8 rounded-full overflow-hidden">
			{body}
		</div>
	);
}
